import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, MatterDeadline } from '../lib/db';
import { DeadlineNotificationToast } from '../components/notifications/DeadlineNotificationToast';
import { differenceInDays, isToday, isPast, startOfDay } from 'date-fns';

interface NotificationContextType {
  overdueCount: number;
  todayCount: number;
  upcomingCount: number;
  checkDeadlines: () => Promise<void>;
  dismissNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
}

interface NotificationProviderProps {
  children: React.ReactNode;
  userId: string | null;
}

interface ActiveNotification {
  id: string;
  deadline: MatterDeadline;
  type: 'overdue' | 'today' | 'upcoming';
}

export function NotificationProvider({ children, userId }: NotificationProviderProps) {
  const navigate = useNavigate();
  const [overdueCount, setOverdueCount] = useState(0);
  const [todayCount, setTodayCount] = useState(0);
  const [upcomingCount, setUpcomingCount] = useState(0);
  const [activeNotifications, setActiveNotifications] = useState<ActiveNotification[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [lastCheck, setLastCheck] = useState<Date | null>(null);
  const [preferences, setPreferences] = useState({
    enableNotifications: true,
    enableToasts: true,
    enableBrowserNotifications: false,
    overdueReminders: true,
    todayReminders: true,
    upcomingDays: 1,
  });

  // Load preferences
  useEffect(() => {
    if (userId) {
      const saved = localStorage.getItem(`notification-prefs-${userId}`);
      if (saved) {
        setPreferences(JSON.parse(saved));
      }
    }
  }, [userId]);

  const dismissNotification = useCallback((id: string) => {
    setActiveNotifications(prev => prev.filter(n => n.id !== id));
    setDismissedIds(prev => new Set(prev).add(id));
  }, []);

  const handleNotificationClick = useCallback((notification: ActiveNotification) => {
    navigate('/matters', { 
      state: { 
        openMatterId: notification.deadline.matter_id, 
        highlightDeadline: notification.deadline.id 
      } 
    });
    dismissNotification(notification.id);
  }, [navigate, dismissNotification]);

  // Use refs to access current values without causing re-renders
  const dismissedIdsRef = useRef(dismissedIds);
  const lastCheckRef = useRef(lastCheck);
  const preferencesRef = useRef(preferences);
  const activeNotificationsRef = useRef(activeNotifications);

  // Update refs when values change
  useEffect(() => {
    dismissedIdsRef.current = dismissedIds;
  }, [dismissedIds]);

  useEffect(() => {
    lastCheckRef.current = lastCheck;
  }, [lastCheck]);

  useEffect(() => {
    preferencesRef.current = preferences;
  }, [preferences]);

  useEffect(() => {
    activeNotificationsRef.current = activeNotifications;
  }, [activeNotifications]);

  const checkDeadlines = useCallback(async () => {
    if (!userId || !preferencesRef.current.enableNotifications) return;

    try {
      // Get all non-completed deadlines
      const deadlines = await db.deadlines.getUpcoming(userId, Math.max(7, preferencesRef.current.upcomingDays));
      
      const today = startOfDay(new Date());
      const overdueDeadlines: MatterDeadline[] = [];
      const todayDeadlines: MatterDeadline[] = [];
      const upcomingDeadlines: MatterDeadline[] = [];
      const notificationsToShow: ActiveNotification[] = [];

      deadlines.forEach(deadline => {
        const dueDate = new Date(deadline.due_date);
        
        if (isPast(dueDate) && !isToday(dueDate)) {
          overdueDeadlines.push(deadline);
          // Show notification for overdue if enabled and not dismissed
          if (preferencesRef.current.overdueReminders && preferencesRef.current.enableToasts && !dismissedIdsRef.current.has(deadline.id)) {
            notificationsToShow.push({
              id: deadline.id,
              deadline,
              type: 'overdue'
            });
          }
        } else if (isToday(dueDate)) {
          todayDeadlines.push(deadline);
          // Show notification for today if enabled and not dismissed
          if (preferencesRef.current.todayReminders && preferencesRef.current.enableToasts && !dismissedIdsRef.current.has(deadline.id)) {
            notificationsToShow.push({
              id: deadline.id,
              deadline,
              type: 'today'
            });
          }
        } else {
          const daysUntil = differenceInDays(dueDate, today);
          if (daysUntil <= preferencesRef.current.upcomingDays) {
            upcomingDeadlines.push(deadline);
            // Show notification for upcoming if enabled and not dismissed
            if (preferencesRef.current.enableToasts && !dismissedIdsRef.current.has(deadline.id)) {
              notificationsToShow.push({
                id: deadline.id,
                deadline,
                type: 'upcoming'
              });
            }
          }
        }
      });

      setOverdueCount(overdueDeadlines.length);
      setTodayCount(todayDeadlines.length);
      setUpcomingCount(upcomingDeadlines.length);

      // Only show new notifications if this isn't the first check
      if (lastCheckRef.current) {
        const newNotifications = notificationsToShow.filter(n => 
          !activeNotificationsRef.current.some(active => active.id === n.id)
        );
        
        // Show in-app notifications
        if (preferencesRef.current.enableToasts) {
          setActiveNotifications(prev => {
            const existingIds = new Set(prev.map(n => n.id));
            const toAdd = newNotifications.filter(n => !existingIds.has(n.id));
            return [...prev, ...toAdd].slice(0, 3); // Max 3 notifications
          });
        }

        // Show browser notifications
        if (preferencesRef.current.enableBrowserNotifications && 'Notification' in window && Notification.permission === 'granted') {
          newNotifications.forEach(notification => {
            const title = notification.type === 'overdue' ? 'Overdue Deadline' : 
                         notification.type === 'today' ? 'Due Today' : 'Upcoming Deadline';
            
            new Notification(title, {
              body: `${notification.deadline.title} - ${notification.deadline.matter_name}`,
              icon: '/pwa-192x192.png',
              tag: notification.id,
              requireInteraction: notification.type === 'overdue'
            }).onclick = () => {
              window.focus();
              handleNotificationClick(notification);
            };
          });
        }
      }

      setLastCheck(new Date());
    } catch (error) {
      console.error('Error checking deadlines:', error);
    }
  }, [userId, handleNotificationClick]);

  // Use ref to store checkDeadlines to avoid recreating interval
  const checkDeadlinesRef = useRef(checkDeadlines);
  useEffect(() => {
    checkDeadlinesRef.current = checkDeadlines;
  }, [checkDeadlines]);

  // Check deadlines on mount and every 5 minutes
  useEffect(() => {
    if (userId) {
      // Initial check
      checkDeadlinesRef.current();
      
      // Set up interval with ref to avoid dependency issues
      const interval = setInterval(() => {
        checkDeadlinesRef.current();
      }, 5 * 60 * 1000); // 5 minutes
      
      // Cleanup function
      return () => {
        clearInterval(interval);
      };
    }
  }, [userId]); // Only depend on userId

  // Reset dismissed IDs daily
  useEffect(() => {
    const resetDismissed = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      
      const timeout = setTimeout(() => {
        setDismissedIds(new Set());
        resetDismissed();
      }, tomorrow.getTime() - now.getTime());

      return () => clearTimeout(timeout);
    };

    const cleanup = resetDismissed();
    return cleanup;
  }, []);

  return (
    <NotificationContext.Provider value={{
      overdueCount,
      todayCount,
      upcomingCount,
      checkDeadlines,
      dismissNotification
    }}>
      {children}
      
      {/* Notification Toasts */}
      <div className="fixed bottom-20 right-4 z-50 pointer-events-none">
        <div className="flex flex-col items-end space-y-2">
          {activeNotifications.map(notification => (
            <div key={notification.id} className="pointer-events-auto">
              <DeadlineNotificationToast
                deadline={notification.deadline}
                type={notification.type}
                onClose={() => dismissNotification(notification.id)}
                onClick={() => handleNotificationClick(notification)}
              />
            </div>
          ))}
        </div>
      </div>
    </NotificationContext.Provider>
  );
}